import { getSiteTemplates } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { LoaderCircle, TrashIcon } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { AddLocation } from '@/features/locations/add-location';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/locations/add/page.tsx: portfolio-only and
// super-admin-only (non-super-admins are sent to the list, as the Next page
// `redirect`ed). Loads the site-templates for the add-from-template form.
export function LocationAdd() {
  const t = useTranslations('admin');
  const { brand: site, slug, loading } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const { data: templates, isLoading } = useSWR('admin/site-templates', () =>
    getSiteTemplates(),
  );

  if (loading || roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (site.siteType !== 'portfolio') {
    return <NotFound />;
  }

  if (!access?.superAdmin) {
    return <Navigate to={`/${slug}/admin/locations`} replace />;
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('locations')}</PageTitle>
        <PageButtons>
          <Button variant="destructive" asChild>
            <Link to={`/${slug}/admin/locations`}>
              <TrashIcon className="size-4" />
              {t('discard')}
            </Link>
          </Button>
        </PageButtons>
      </PageHeader>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoaderCircle className="text-primary size-8 animate-spin" />
        </div>
      ) : (
        <AddLocation templates={templates ?? []} />
      )}
    </PageBox>
  );
}
